// RAPIFAST :: "Mis documentos" del conductor — FLUJO REAL (Supabase + Storage privado).
// Usa las RPCs de 0024 y el helper de Storage de 0025. Sin datos ficticios.
// Requiere 0024 + 0025 aplicadas; si no lo están, se muestra el error real del servidor.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, RefreshCw, Ban, CheckCircle2, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader } from "@/components/kit";
import { Badge, SectionCard } from "@/components/verificationKit";
import { uploadVerificationDocument, deleteVerificationDocument } from "@/lib/verificationStorage";
import {
  DRIVER_DOC_TYPES, DRIVER_DOC_ALWAYS, DRIVER_DOC_EXPIRY, DRIVER_DOC_LABELS,
  VERIF_STATUS_META, getMyDriverDocuments, upsertMyDriverDocument, isExpired, docDisplayStatus,
} from "@/lib/verificationApi";
import { evaluateTecnoRequirement } from "@/lib/vehicleRule";

const inputCls =
  "rounded-lg border border-[#333] bg-[#1F1F1F] px-2.5 py-1.5 text-xs text-gray-200 outline-none focus:border-[#FF6A00]";

function DocCard({ docType, row, requireExpiry, onUpload, busy }) {
  const fileRef = useRef(null);
  const [docNumber, setDocNumber] = useState(row?.doc_number || "");
  const [expiresAt, setExpiresAt] = useState(row?.expires_at || "");
  const [file, setFile] = useState(null);
  const display = docDisplayStatus(row) || "NOT_UPLOADED";
  const uploaded = !!row;
  const canReplace = !uploaded || ["REJECTED", "EXPIRED"].includes(display) || display === "VERIFIED" || display === "PENDING";

  const submit = () => {
    if (!file) { toast.error("Selecciona un archivo (imagen o PDF) para subir"); return; }
    if (requireExpiry && !expiresAt) { toast.error("Indica la fecha de vencimiento del documento"); return; }
    onUpload({ docType, file, docNumber, expiresAt: expiresAt || null, previousPath: row?.storage_path });
    setFile(null);
  };

  return (
    <div className="rounded-xl border border-[#1e1e21] bg-[#101012] p-3.5" data-testid={`my-doc-${docType.toLowerCase()}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold text-white">{DRIVER_DOC_LABELS[docType]}</div>
        {uploaded ? (
          <Badge tone={VERIF_STATUS_META[display]} testId={`my-doc-status-${docType.toLowerCase()}`}>
            {VERIF_STATUS_META[display].label}
          </Badge>
        ) : (
          <span className="rounded-full border border-gray-500/40 bg-gray-500/10 px-2.5 py-1 text-xs font-bold text-gray-400"
            data-testid={`my-doc-status-${docType.toLowerCase()}`}>
            Sin cargar
          </span>
        )}
      </div>

      {row?.expires_at && (
        <div className={`mt-1 text-xs ${isExpired(row.expires_at) ? "font-bold text-red-400" : "text-gray-500"}`}>
          Vence: {row.expires_at}{isExpired(row.expires_at) && " · VENCIDO"}
        </div>
      )}
      {display === "REJECTED" && row?.review_notes && (
        <div className="mt-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300"
          data-testid={`my-doc-reject-note-${docType.toLowerCase()}`}>
          Observación del revisor: {row.review_notes}
        </div>
      )}
      {display === "VERIFIED" && !isExpired(row?.expires_at) && (
        <p className="mt-2 text-xs text-emerald-400/80">Verificado por el administrador. Reemplazarlo lo devolverá a revisión.</p>
      )}

      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <input className={inputCls} placeholder="Número de documento (opcional)" value={docNumber}
          onChange={(e) => setDocNumber(e.target.value)} data-testid={`my-doc-number-${docType.toLowerCase()}`} />
        {requireExpiry && (
          <input type="date" className={inputCls} value={expiresAt || ""}
            onChange={(e) => setExpiresAt(e.target.value)} data-testid={`my-doc-expiry-${docType.toLowerCase()}`} />
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
          onChange={(e) => setFile(e.target.files[0] || null)} data-testid={`my-doc-file-input-${docType.toLowerCase()}`} />
        <button onClick={() => fileRef.current?.click()}
          data-testid={`my-doc-choose-button-${docType.toLowerCase()}`}
          className="flex items-center gap-1.5 rounded-lg border border-[#333] px-3 py-1.5 text-xs font-bold text-gray-300 hover:bg-[#1F1F1F]">
          <Upload size={13} /> {file ? "Cambiar archivo" : "Elegir archivo"}
        </button>
        {file && <span className="max-w-[150px] truncate text-xs text-gray-400">{file.name}</span>}
        <button onClick={submit} disabled={!canReplace || busy}
          data-testid={`my-doc-upload-button-${docType.toLowerCase()}`}
          className="flex items-center gap-1.5 rounded-lg bg-[#FF6A00]/15 px-3 py-1.5 text-xs font-bold text-[#FF6A00] hover:bg-[#FF6A00]/25 disabled:opacity-40">
          {busy ? <Loader2 size={13} className="animate-spin" /> : (uploaded ? <RefreshCw size={13} /> : <Upload size={13} />)}
          {uploaded ? "Reemplazar documento" : "Subir documento"}
        </button>
      </div>
    </div>
  );
}

export default function DriverDocuments() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [docs, setDocs] = useState([]);
  const [vehicleInfo, setVehicleInfo] = useState(() => evaluateTecnoRequirement(null, true));
  const [busyType, setBusyType] = useState(null);

  const byType = (t) => docs.find((d) => d.doc_type === t);
  const requiresTecno = vehicleInfo.requiresTecno;
  const requiredTypes = requiresTecno ? [...DRIVER_DOC_ALWAYS, "TECNOMECANICA"] : DRIVER_DOC_ALWAYS;

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      // Todos los vehículos activos: un error de consulta NO se interpreta como "solo moto".
      const { data: vehs, error: vehErr } = await supabase
        .from("vehicles").select("type").eq("driver_id", user.id).eq("is_active", true);
      setVehicleInfo(evaluateTecnoRequirement(vehs, vehErr));
      const rows = await getMyDriverDocuments();
      setDocs(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setLoadError(err?.message || "No se pudieron cargar tus documentos.");
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async ({ docType, file, docNumber, expiresAt, previousPath }) => {
    setBusyType(docType);
    try {
      const path = await uploadVerificationDocument({ kind: "driver", docType, file });
      await upsertMyDriverDocument({ docType, storagePath: path, docNumber, issuedAt: null, expiresAt });
      // Best-effort: borrar el archivo anterior para no dejar huérfanos en el bucket.
      if (previousPath && previousPath !== path) {
        try { await deleteVerificationDocument(previousPath); } catch { /* no bloqueante */ }
      }
      toast.success("Documento subido. Queda pendiente de revisión del administrador.");
      await load();
    } catch (err) {
      toast.error(err?.message || "No se pudo subir el documento. Inténtalo de nuevo.");
    } finally {
      setBusyType(null);
    }
  };

  // Habilitación (solo presentación; la decisión real es is_driver_habilitado en el servidor).
  const missing = requiredTypes.filter((t) => {
    const r = byType(t);
    return !r || r.status !== "VERIFIED" || isExpired(r.expires_at);
  });
  const enabled = missing.length === 0;

  if (loading) return <Loader label="Cargando tus documentos…" />;

  return (
    <div data-testid="driver-documents-real-page">
      <PageHeader
        title="Mis documentos"
        subtitle="Sube tus soportes al almacenamiento privado. El administrador los revisa antes de habilitarte."
        testId="driver-documents-header"
      />

      {loadError ? (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          data-testid="driver-documents-load-error">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-bold">No se pudieron cargar tus documentos.</div>
            <div className="mt-0.5 text-red-300/80">{loadError}</div>
            <button onClick={load} data-testid="driver-documents-retry"
              className="mt-2 rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-bold text-red-200 hover:bg-red-500/10">
              Reintentar
            </button>
          </div>
        </div>
      ) : (
        <div className={`mb-5 flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold ${
          enabled ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" : "border-red-500/50 bg-red-500/10 text-red-400"
        }`} data-testid="driver-enabled-banner">
          {enabled ? <CheckCircle2 size={16} /> : <Ban size={16} />}
          {enabled
            ? "Documentos al día. El sistema confirmará tu habilitación al recibir ofertas."
            : `Bloqueado para recibir asignaciones — pendientes: ${missing.map((k) => DRIVER_DOC_LABELS[k]).join(", ")}`}
        </div>
      )}

      <div className="mb-5 flex items-start gap-3 rounded-2xl border border-[#27272A] bg-[#141416] px-4 py-3 text-xs text-gray-400"
        data-testid="driver-documents-privacy-note">
        <ShieldCheck size={16} className="mt-0.5 shrink-0 text-[#FF6A00]" />
        Tus archivos se guardan en un almacenamiento privado y solo tú y el administrador pueden abrirlos
        mediante enlaces temporales. Nunca compartas claves ni PIN; no los solicitamos.
      </div>

      {vehicleInfo.status === "error" && (
        <div className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-amber-300"
          data-testid="driver-vehicle-check-error">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            No pudimos consultar tus vehículos activos. Por precaución se muestra la revisión técnico-mecánica;
            el servidor decide si es obligatoria según tu vehículo registrado.
            <button onClick={load} data-testid="driver-vehicle-check-retry"
              className="ml-2 rounded-lg border border-amber-500/40 px-2 py-0.5 font-bold hover:bg-amber-500/10">
              Reintentar
            </button>
          </div>
        </div>
      )}
      {vehicleInfo.activeCount > 1 && (
        <div className="mb-5 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-amber-300"
          data-testid="driver-vehicle-multiple-note">
          Tienes {vehicleInfo.activeCount} vehículos activos.
          {requiresTecno
            ? " Al menos uno no es motocicleta, por eso la revisión técnico-mecánica es obligatoria."
            : " Todos son motocicletas, por eso la revisión técnico-mecánica no aplica."}
        </div>
      )}
      {vehicleInfo.status === "none" && (
        <div className="mb-5 rounded-2xl border border-[#27272A] bg-[#141416] px-4 py-3 text-xs text-gray-400"
          data-testid="driver-vehicle-none-note">
          No tienes un vehículo activo registrado. Sin vehículo activo no recibirás ofertas, aunque tus documentos estén verificados.
        </div>
      )}

      <SectionCard title="Documentos exigidos" testId="driver-documents-list">
        <p className="mb-3 text-xs text-gray-500">
          Obligatorios: cédula, licencia, SOAT y tarjeta de propiedad
          {vehicleInfo.status === "error"
            ? ", y revisión técnico-mecánica si alguno de tus vehículos activos no es motocicleta."
            : requiresTecno
              ? ", y revisión técnico-mecánica (tienes un vehículo activo que no es motocicleta)."
              : " (la técnico-mecánica no aplica: no tienes vehículos activos distintos de motocicleta)."}
          {" "}Subir un documento lo deja <b>pendiente</b>; solo el administrador puede verificarlo.
        </p>
        <div className="space-y-3">
          {DRIVER_DOC_TYPES.map((t) => {
            if (t === "TECNOMECANICA" && !requiresTecno) {
              return (
                <div key={t} className="rounded-xl border border-[#1e1e21] bg-[#101012] px-3.5 py-3 text-xs text-gray-500"
                  data-testid="my-doc-tecnomecanica-na">
                  {DRIVER_DOC_LABELS[t]}: no aplica (sin vehículos activos distintos de motocicleta).
                </div>
              );
            }
            return (
              <DocCard key={t} docType={t} row={byType(t)} requireExpiry={DRIVER_DOC_EXPIRY.includes(t)}
                onUpload={handleUpload} busy={busyType === t} />
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}
